import {Component, EventEmitter, Input, Output, OnInit, DestroyRef, inject} from '@angular/core';
import {FormGroup, Validators} from '@angular/forms';
import {FormFieldBase} from '../../../interfaces/form-field-base/form-field-base';
import {MatDialog} from '@angular/material/dialog';
import {ViewOverridesDialogComponent} from '@emm/shared/dialogs/view-overrides-dialog/view-overrides-dialog.component';
import {BehaviorSubject} from 'rxjs';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {TagService} from '@emm/core/services/tag.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {take} from 'rxjs/operators';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {formatFieldName} from '@emm/core/util/string-format.util';

@Component({
    selector: 'emm-dynamic-menu-form-field',
    templateUrl: './dynamic-menu-form-field.component.html',
    styleUrls: ['./dynamic-menu-form-field.component.scss'],
    standalone: false
})
export class DynamicMenuFormFieldComponent<T> implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    @Input() formField!: FormFieldBase<string>;
    @Input() form!: FormGroup;
    @Input() item: any;
    @Input() itemTag: string;
    @Input() itemTagPath: string;
    @Input() nodeId: string;
    @Input() selectedSubmenu: string;
    @Input() selectedLocation: string;
    @Input() selectedFeatureScope: string;
    @Input() effectiveDate: string;
    @Input() selectedLanguage: string;
    @Input() isCascade: boolean;
    @Output() removeLocalAttribute = new EventEmitter<FormFieldBase<string>>();
    attribute: string;
    overrideTooltip = new BehaviorSubject<string>(null);
    isDarkMode: boolean;
    selectedSearchSubmenu = new BehaviorSubject<string>(null);
    hasParentOverride$ = new BehaviorSubject<boolean>(false);
    parentOverride = new BehaviorSubject<string>('');
    useFieldRules$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_FIELD_RULES);

    constructor(
        private dialog: MatDialog,
        private api: MenuApiService,
        private sharedFacadeService: SharedFacadeService,
        private tagService: TagService,
        private notificationService: NotificationService,
        private launchDarklyService: LaunchDarklyService,
    ) {}

    ngOnInit() {
        this.sharedFacadeService.currentTheme$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(theme => theme === 'dark-theme' ?
                this.isDarkMode = true : this.isDarkMode = false);
        this.attribute = this.formField.jsonProperty;

        // Format field name with proper spacing
        if (this.formField.name) {
            this.formField.name = formatFieldName(this.formField.name);
        }

        const filters = this.sharedFacadeService.getSelectedSearchSubmenu();
        if (filters?.submenu && filters?.submenu !== 'null') {
            this.selectedSearchSubmenu.next(filters.submenu);
        }
        if (this.attribute && this.formField.scope === 'LOCAL' && this.selectedLocation !== 'CHAIN' &&
            this.selectedFeatureScope !== 'Viewer' && !this.isCascade) {
            this.api.getParentOverrides(this.attribute, this.selectedLocation, this.selectedSubmenu, this.effectiveDate,
                this.itemTagPath, this.selectedLanguage).pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                    next: (result) => {
                        if (result && result !== this.selectedLocation) {
                            this.parentOverride.next(result);
                            this.hasParentOverride$.next(true);
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
        }
    }

    get isValid() {
        return this.form.controls[this.formField.jsonProperty].valid;
    }

    getFieldTooltip(): string {
        if (this.formField.jsonProperty === 'disabled' && this.item) {
            const disabledReasons = this.item.disabledReasons;
            if (Array.isArray(disabledReasons) && disabledReasons.length > 0) {
                return disabledReasons.join('\n');
            }
            if (this.item.disabledSource) {
                return this.item.disabledSource;
            }
        }
        return this.formField.description || '';
    }

    getInputCssClass(): string {
        let hasChanged;
        const isDropdown = (this.formField.type === 'SELECT' || this.formField.type === 'MULTI_SELECT' ||
            this.formField.type === 'BOOL');
        if (this.formField.scope !== 'LOCAL') {
            hasChanged = this.form?.controls[this.formField.jsonProperty]?.dirty;
        }
        if (this.formField.isOverride || hasChanged || (this.selectedFeatureScope === 'Viewer' && isDropdown)) {
            return 'attr-global-overridden';
        }
        if (this.form?.controls[this.formField.jsonProperty]?.disabled) {
            return 'attr-disabled';
        }

        return 'attr-global';
    }

    getDarkModeInputCssClass(): string {
        let hasChanged;
        if (this.formField?.scope !== 'LOCAL') {
            hasChanged = this.form?.controls[this.formField.jsonProperty]?.dirty;
        }
        if (this.formField?.isOverride || hasChanged) {
            return 'dark-attr-global-overridden';
        }
        if (this.form?.controls[this.formField.jsonProperty]?.disabled) {
            return 'dark-attr-disabled';
        }

        return 'dark-attr-global';
    }

    handleRemoveLocalAttribute(formField: FormFieldBase<string>) {
        this.removeLocalAttribute.emit(formField);
    }

    openViewOverridesDialog() {
        const menuViewerSubmenu = this.itemTagPath.split('/')[1];
        this.dialog.open(ViewOverridesDialogComponent, {
            data: {
                attribute: this.formField,
                itemPath: this.itemTagPath,
                selectedSubmenu: this.selectedSearchSubmenu.value && this.selectedSearchSubmenu.value !== 'null' ?
                    this.selectedSearchSubmenu.value : this.selectedSubmenu ?? menuViewerSubmenu,
                effectiveDate: this.effectiveDate,
                currentValue: this.form.controls[this.formField.jsonProperty]?.value,
                location: this.selectedLocation,
            },
            disableClose: true,
            position: {top: '100px'},
            width: '650px'
        });
    }

    setTooltipToFieldValue(jsonProperty: string) {
        this.overrideTooltip.next(this.form.controls[jsonProperty].value);
    }

    numberValidation(defaultValue): void {
        if (defaultValue === 'retailModifiedItemId' || defaultValue === 'subsGroupId') {
            this.rmiAndSubsGroupValidation();
        }
        if (this.form.controls[defaultValue].value
            && this.form.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp('^[0-9]+(.[0-9]{1,2})?$');

            if (pattern.test(this.form.controls[defaultValue].value)) {
                this.form.controls[defaultValue].setErrors(null);
                return;
            }

            this.form.controls[defaultValue].setErrors({pattern: true});
            this.notificationService.error('Please enter a number');
        }
    }

    imageValidation(defaultValue): void {
        if (this.form.controls[defaultValue].value
            && this.form.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp(
                '^https?:\\/\\/' +                              // Match http:// or https://
                '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
                '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
                '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
                '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
                '(\\?.*)?(#.*)?$',                              // Optional query and fragment
                'i'                                             // Case-insensitive
            );

            if (pattern.test(this.form.controls[defaultValue].value)) {
                this.form.controls[defaultValue].setErrors(null);
                return;
            }

            this.form.controls[defaultValue].setErrors({pattern: true});
            this.notificationService.error('Invalid URL format');
        }
    }

    findTagExists(): void {
        if (this.form?.controls['tag']?.value?.includes(' ')) {
            this.form?.controls['tag']?.setErrors({incorrect: true});
            this.notificationService.error('Tags cannot contain spaces');
        }
        if (this.form?.controls['tag']?.valid) {
            if (this.form?.controls['tag']?.value === this.form?.controls['tag']?.value?.toUpperCase()) {
                this.tagService.getItemTags(this.form?.get('tag')?.value)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe({
                        next: (res) => {
                            if (res.response === true) {
                                this.form?.controls['tag']?.setErrors({tagExists: true});
                                this.notificationService.error('Tag already exists');
                            }
                        },
                        error: (error) => {
                            this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        }
                    });
            } else {
                this.form?.controls['tag']?.setErrors({incorrect: true});
                this.notificationService.error('Tag should be uppercase');
            }
        }
    }

    defaultTagValidation(): void {
        if (this.formField?.jsonProperty === 'defaultTag' && this.formField['allowedValues']) {

            if (this.formField['allowedValues'].includes(this.form.controls['defaultTag']?.value)) {
                this.form.controls['defaultTag'].setErrors(null);
                return;
            }

            this.form.controls['defaultTag'].setErrors({pattern: true});
            this.notificationService.error(`Please enter one of these values: ${this.formField['allowedValues'].join(', ')}`);
        }
    }

    rmiAndSubsGroupValidation() {
        if (this.form?.controls) {
            const rmi = this.form.controls['retailModifiedItemId'];
            const subsGroupId = this.form.controls['subsGroupId'];

            const rmiValue = rmi?.value;
            const subsGroupIdValue = subsGroupId?.value;

            if (rmiValue && !subsGroupIdValue) {
                subsGroupId.setErrors({ pattern: true });
                subsGroupId.setValidators([Validators.required]);
                this.notificationService.error(`If an item has an RMI, it must also have a Subs Group ID`);
            } else {
                subsGroupId.setErrors(null)
                subsGroupId.clearValidators();
            }
        }
    }

    pickValidationFunction(formField) {
        if (formField.jsonProperty === 'defaultTag') {
            this.defaultTagValidation();
        } else {
            this.findTagExists();
        }
    }
}
